import styled from '@emotion/styled';
import {
    ActionIcon,
    Anchor,
    Box,
    Card,
    Divider,
    Group,
    Loader,
    MantineColor,
    Popover,
    Progress,
    Tabs,
    Text,
    ThemeIcon,
    useMantineTheme,
} from '@mantine/core';
import { useCompany } from '@root/Components/Router/CompanyContent';
import { AuthenticationService } from '@root/Services/AuthenticationService';
import { useDi, useDiContainer } from '@root/Services/DI';
import { useEvent, useEventValue, useToggle } from '@root/Services/EventEmitter';
import { JobService } from '@root/Services/Jobs/JobService';
import { NavigationService, useNav } from '@root/Services/NavigationService';
import { BasicRouteLoader } from '@root/Services/Router/BasicRouteLoader';
import { Fragment, useState, useCallback, useEffect, useMemo } from 'react';
import { Bell, BellRinging, ListCheck, Mail } from 'tabler-icons-react';
import { ActivityDetailsOpener } from '../ActionDetails';
import { ActivityItemModel, JobStatus } from './ActivityTypes';
import { ActivityPanelModel } from './Models';
import { ActivityPoller } from './ActivityPoller';
import { useFmtSvc } from '@root/Services/FormatService';
import { useRerender } from '@react-hookz/web';
import { IndexedNotification } from '@apis/Notification/model';

export function ActivityPanelOpener() {
    const company = useCompany();
    const routeLoader = useDi(BasicRouteLoader);
    const route = useEventValue(routeLoader.routeMeta);
    const depth = route?.length ?? 0;
    const di = useDiContainer();
    const model = useMemo(() => di.resolve(ActivityPanelModel), []);
    const poller = useMemo(() => di.resolve(ActivityPoller), []);
    const { user } = useDi(AuthenticationService);
    useEffect(() => {
        poller.load(user, company);
    }, []);
    useEffect(() => {
        poller.load(user, company);
        if (user && company) {
            model.poll();
        }
    }, [user, company]);

    return (
        <>
            {route?.map((route, i) => (
                <ActivityPanelPopover key={route.currentPathWithoutParams} model={model} visible={i + 1 === depth} />
            ))}
        </>
    );
}

function ActivityPanelPopover({ visible, model }: { visible: boolean; model: ActivityPanelModel }) {
    const [opened, { close, toggle }] = useToggle(false);
    useEventValue(model.jobsUpdated);
    const [selected, setSelected] = useState<JobStatus>();
    const hidePopover = useCallback(() => {
        if (opened) {
            model.pause();
            close();
        }
    }, [close, model, opened]);
    const togglePopover = useCallback(() => {
        if (visible) {
            if (!opened) {
                model.poll();
                model.seeJobs();
            }
            toggle();
        }
    }, [opened, toggle, visible]);
    useEvent(model.toggleRequested, togglePopover);
    const selectJob = useCallback(
        (jobStatus: JobStatus) => {
            hidePopover();
            setSelected(jobStatus);
        },
        [setSelected, hidePopover]
    );

    return (
        <Box hidden={!visible}>
            <Popover opened={opened} onClose={hidePopover} withinPortal radius="lg">
                <Popover.Target>
                    <ActionIcon data-atid="ActivityLogButton" onClick={togglePopover}>
                        <ActivityPanelIcon model={model} />
                    </ActionIcon>
                </Popover.Target>
                <Popover.Dropdown p={0} sx={{ borderWidth: 0, background: 'transparent' }} pr="xl">
                    <ActivityPanel model={model} visible={opened} onClose={close} onSelect={selectJob} />
                </Popover.Dropdown>
            </Popover>
            {!visible ? null : <ActivityDetailsOpener item={selected?.job} onClose={() => setSelected(undefined)} cascade />}
        </Box>
    );
}

function ActivityPanelIcon({ model }: { model: ActivityPanelModel }) {
    const theme = useMantineTheme();
    useEvent(model.jobsUpdated);
    useEvent(model.notificationsUpdated);
    const count = model.newJobs.count + model.newNotifs.count;
    const important = model.newJobs.important || model.newNotifs.important;
    const Icon = !important ? Bell : BellRinging;
    const iconColor = count > 0 ? theme.colors.primary[6] : theme.black;
    const mode = important ? 'important' : count > 0 ? ('dotted' as const) : undefined;
    return (
        <IconContainer mode={mode}>
            <Icon style={{ color: iconColor }} />
        </IconContainer>
    );
}

interface IActivityPanelProps {
    model: ActivityPanelModel;
    visible: boolean;
    onClose: () => void;
    onSelect: (jobStatus: JobStatus) => void;
}
function ActivityPanel(props: IActivityPanelProps) {
    return !props.visible ? <></> : <InternalActivityPanel {...props} />;
}
function InternalActivityPanel(props: IActivityPanelProps) {
    const { model, visible, onClose } = props;
    const routeLoader = useDi(BasicRouteLoader);
    const nav = useNav(routeLoader.getTopRouteMeta());
    const { getDescendUrl, goto } = nav;
    const allActivityUrl = getDescendUrl('actionslog', { section: 'Activity' });
    const allNotificationsUrl = getDescendUrl('actionslog', { section: 'Notifications' });
    const rerender = useRerender();
    const viewAll = useCallback(
        (section: 'Notifications' | 'Activity', e: { preventDefault: () => void }) => {
            const endPtName = routeLoader.getTopRouteMeta().endpointInfo?.name?.toLowerCase() ?? '';
            const atLog = endPtName === 'actionslog';
            const currSection = !atLog ? null : nav.getData('section')?.section;
            const atSection = (atLog && !currSection && section === 'Activity') || currSection === section;
            if (atLog && !atSection) {
                nav.move('actionslog', { section });
            } else if (!atSection) {
                goto(section === 'Activity' ? allActivityUrl : allNotificationsUrl);
            }
            onClose();
            e.preventDefault();
        },
        [onClose]
    );

    const onTabChange = useCallback(
        (section: 'Activity' | 'Notifications') => {
            model.section = section;
            rerender();
        },
        [rerender]
    );
    const viewJobs = useCallback((e: { preventDefault: () => void }) => viewAll('Activity', e), [viewAll]);
    const viewNotifications = useCallback((e: { preventDefault: () => void }) => viewAll('Notifications', e), [viewAll]);
    useEvent(model.jobsUpdated);
    useEvent(model.notificationsUpdated);
    const { colors } = useMantineTheme();
    const jobStats = model.newJobs;
    const jobLbl = jobStats.count ? `Activity (${jobStats.count})` : 'Activity';
    const jobColor = jobStats.important ? colors.error[6] : jobStats.count > 0 ? colors.primary[6] : colors.gray[6];
    const notifStats = model.newNotifs;
    const notifLbl = notifStats.count ? `Notifications (${notifStats.count})` : 'Notifications';
    const notifColor = notifStats.important ? colors.error[6] : notifStats.count > 0 ? colors.primary[6] : colors.gray[6];

    return !visible ? (
        <></>
    ) : (
        <Card component={Container} radius="lg" p={0} withBorder shadow="lg">
            <Toolbar>
                <Tabs pt={8} onTabChange={onTabChange} value={model.section}>
                    <Tabs.List px="sm">
                        <Tabs.Tab value="Notifications" icon={<Mail size={16} stroke={notifColor} />}>
                            {notifLbl}
                        </Tabs.Tab>
                        <Tabs.Tab value="Activity" icon={<ListCheck size={16} stroke={jobColor} />}>
                            {jobLbl}
                        </Tabs.Tab>
                    </Tabs.List>
                </Tabs>
            </Toolbar>
            {model.section === 'Notifications' ? <NotificationsList {...props} nav={nav} /> : <JobsList {...props} nav={nav} />}
            <Divider />
            <Toolbar>
                <Text align="center" py="xs">
                    {model.section === 'Notifications' ? (
                        <Anchor data-atid={'ActivityLogLinkAnchor'} size="sm" href={allNotificationsUrl} onClick={viewNotifications}>
                            View All Notifications
                        </Anchor>
                    ) : (
                        <Anchor data-atid={'ActivityLogLinkAnchor'} size="sm" href={allActivityUrl} onClick={viewJobs}>
                            View All Activity
                        </Anchor>
                    )}
                </Text>
            </Toolbar>
        </Card>
    );
}

function JobsList(props: IActivityPanelProps & { nav: NavigationService }) {
    const { nav, onSelect, onClose, model } = props;
    const jobService = useDi(JobService);

    const jobs = useEventValue(model.jobsUpdated);
    const jobGroups = jobs?.reduce(
        (result, item) => {
            const { inProgress } = jobService.getStatusInfo(item.status);
            if (inProgress) {
                result.inProgress.push(item);
            } else {
                result.finished.push(item);
            }
            return result;
        },
        { inProgress: [], finished: [] } as { inProgress: JobStatus[]; finished: JobStatus[] }
    );
    return (
        <>
            <Content data-atid={'ActivityLogContainer'}>
                {jobs?.length ? null : <Text align="center">No recent activity</Text>}
                <JobActivitySection
                    nav={nav}
                    onSelect={onSelect}
                    onClose={onClose}
                    items={jobGroups?.inProgress}
                    model={model}
                    title={jobGroups?.finished.length ? 'In Progress' : ''}
                    data-atid="ActivityLogInProgressSection"
                />
                <JobActivitySection
                    nav={nav}
                    onSelect={onSelect}
                    onClose={onClose}
                    items={jobGroups?.finished}
                    model={model}
                    title={jobGroups?.inProgress.length ? 'Finished' : ''}
                    data-atid="ActivityLogFinishedSection"
                />
            </Content>
        </>
    );
}

type NotificationItem = { notification: IndexedNotification } & ActivityItemModel;
function NotificationsList(props: IActivityPanelProps & { nav: NavigationService }) {
    const { model, ...baseSectionProps } = props;

    const updates = useEventValue(model.notificationsUpdated);
    const { alerts, reports, seen } = useMemo(() => {
        const notifications = model.getNotificationDetails();
        const result: { alerts: NotificationItem[]; reports: NotificationItem[]; seen: NotificationItem[] } = {
            alerts: [],
            reports: [],
            seen: [],
        };
        const getSortKey = (item: NotificationItem) => item.notification.OpenedAt ?? item.notification.Date ?? '';
        notifications.sort((a, b) => getSortKey(b).localeCompare(getSortKey(a)));
        for (const item of notifications) {
            const section = item.notification.OpenedAt ? 'seen' : item.notification.TriggerType === 'Threshold' ? 'alerts' : ('reports' as const);
            result[section].push(item);
        }
        return result;
    }, [updates]);
    const baseProps = { model, ...baseSectionProps };
    const sections = [
        { items: alerts, title: 'Alerts' },
        { items: reports, title: 'Scheduled Reports' },
        { items: seen, title: 'Recently Viewed' },
    ]
        .map((s) => ({ ...baseProps, ...s }))
        .filter((s) => s.items.length > 0);
    const isEmpty = !sections?.length;
    return (
        <Content>
            {isEmpty ? (
                <Text align="center">No recent notifications</Text>
            ) : (
                sections.map((s) => <NotificationActivitySection key={s.title} {...s} />)
            )}
        </Content>
    );
}

function NotificationActivitySection(props: IActivitySectionProps & { items: NotificationItem[] }) {
    const { title } = props;
    const handleSelectNoop = useCallback(() => {}, []);
    return (
        <>
            <Text pl="md" pt={4}>
                {title}
            </Text>
            {props.items.map(({ notification, ...item }, i) => (
                <ActivityItem key={i} activityModel={item} date={notification.Date} {...props} onClick={handleSelectNoop} />
            ))}
        </>
    );
}

function JobActivitySection(props: IActivitySectionProps & { items?: JobStatus[]; onSelect: (jobStatus: JobStatus) => void }) {
    const { title, items, onSelect, onClose, model, nav } = props;
    const activityModels = useMemo(() => {
        const result: { job: JobStatus; activityModel: ActivityItemModel }[] = [];
        if (!!items?.length) {
            for (const item of items) {
                const activityModel = model.activityItemAdapter.getActivityModel(item);
                result.push({ job: item, activityModel });
            }
            result.sort((a, b) => (b.job.status.lastDate ?? 0) - (a.job.status.lastDate ?? 0));
        }
        return result;
    }, [items]);

    return (
        <>
            {activityModels?.length ? (
                <>
                    <Text pl="md" pt={4}>
                        {title}
                    </Text>
                </>
            ) : null}
            {activityModels?.map(({ activityModel, job: j }, i) => (
                <Fragment key={j.job.Id!}>
                    {i > 0 ? null : null}
                    <JobActivityItem nav={nav} onClick={() => onSelect(j)} onClose={onClose} activityModel={activityModel} item={j} />
                </Fragment>
            ))}
        </>
    );
}

interface IActivitySectionProps {
    title: string;
    onClose: () => void;
    model: ActivityPanelModel;
    nav: NavigationService;
}

function JobActivityItem({ item, activityModel, ...props }: IActivityItemProps & { item: JobStatus }) {
    const { progress: multiJobProgress } = activityModel;
    const singleJobProgress =
        item.job.Progress && item.job.Progress.Total && item.job.Progress.Progress
            ? Math.min(1, item.job.Progress.Progress / item.job.Progress.Total)
            : null;
    const progress = singleJobProgress === null ? multiJobProgress : singleJobProgress;

    return <ActivityItem progress={progress} date={item.job.QueuedAt!} activityModel={activityModel} {...props} />;
}

interface IActivityItemProps {
    progress?: number;
    date?: string;
    onClick: () => void;
    onClose: () => void;
    nav: NavigationService;
    activityModel: ActivityItemModel;
}
function ActivityItem({ progress, date, onClick, onClose, nav, activityModel }: IActivityItemProps) {
    const fmtSvc = useFmtSvc();
    const { icon: Icon, inProgress, hasErrors, ...model } = activityModel;
    const ago = fmtSvc.timeAgo(new Date(date!));
    const finish = inProgress ? 'In progress' : hasErrors ? 'Finished with errors' : 'Finished';
    const visualStatus = model.visualStatus ?? (hasErrors ? 'warning' : 'success');
    const statusText = model.action?.description ?? finish;
    const [actionStarted, setActionStarted] = useState(false);
    const handleClick = async () => {
        if (model.action?.execute) {
            setActionStarted(true);
            try {
                await model.action.execute(nav, onClose);
            } finally {
                setActionStarted(false);
            }
        } else {
            onClick();
        }
    };

    const iconColor: MantineColor = visualStatus === 'warning' ? 'warning' : visualStatus === 'unimportant' ? 'gray' : 'success';
    const statusColor: MantineColor = visualStatus === 'warning' ? 'warning' : visualStatus === 'unimportant' ? 'primary' : 'success';

    return (
        <Box component={ItemLink} py="xs" px="md" onClick={handleClick}>
            <Group noWrap>
                <ThemeIcon color={iconColor} variant="light" size="xl" radius="xl">
                    {actionStarted ? <Loader /> : <Icon />}
                </ThemeIcon>
                <Box>
                    <Group noWrap>
                        <Text size="sm">{model.title}</Text>
                        <Text size="xs" color="dimmed">
                            {ago}
                        </Text>
                    </Group>
                    <Text size="xs">{model.description}</Text>
                    {inProgress && model.showProgress ? <Progress value={Math.min(100, (progress ?? 0) * 100)} /> : null}
                    {!inProgress || !model.showProgress ? (
                        <Text size="xs" color={statusColor}>
                            {statusText}
                        </Text>
                    ) : null}
                </Box>
            </Group>
        </Box>
    );
}

const ItemLink = styled.a`
    display: block;
    border-radius: ${(p) => p.theme.radius.sm}px;
    cursor: pointer;
    :hover {
        background: ${(p) => p.theme.colors.primary[0]};
    }
`;

const Container = styled.div`
    height: 60vh;
    min-height: 600px;
    width: 400px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
`;

const IconContainer = styled.div<{ mode?: 'dotted' | 'important' }>`
    position: relative;
    width: ${(p) => (p.mode ? '30px' : 'auto')};
    height: ${(p) => (p.mode ? '30px' : 'auto')};
    ::after {
        content: '';
        height: 10px;
        width: 10px;
        border-radius: 5px;
        background: ${(p) => (p.mode === 'important' ? p.theme.colors.error[6] : p.mode === 'dotted' ? p.theme.colors.success[6] : undefined)};
        position: absolute;
        bottom: 0px;
        right: 0px;
    }
`;

const Toolbar = styled.div`
    flex: 0;
`;
const Content = styled.div`
    flex: 1;
    min-height: 0;
    height: 100%;
    overflow: auto;
    padding: ${(p) => p.theme.spacing.xs / 2}px;
`;
